/**
 * Created: 2026-10-05
 * Initial commit: Add invoice request detail dialog with orders grid and file upload
 *
 * Dialog component showing invoice request details, associated orders,
 * and file upload field for invoice PDFs.
 */

import { gql } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import { FileField, message } from '@homemaster/jslib'
import CloseIcon from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'
import UploadIcon from '@mui/icons-material/Upload'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material'
import { DataGridPro, type GridColDef } from '@mui/x-data-grid-pro'
import {
  INVOICE_STATUS_CHIP,
  INVOICEE_TYPE_LABEL,
  ORDER_TYPE_LABEL,
} from '@tc-app/shared/labels'
import { useEffect, useState } from 'react'
import {
  GetInvoiceRequestDetailDocument,
  type GetInvoiceRequestDetailQuery,
  InvoiceStatus,
  UploadInvoiceDocument,
} from '../gql/graphql'
import OrderDetailCell from './OrderDetailCell'
import UserAvatar from './UserAvatar'

gql`
  query GetInvoiceRequestDetail($id: UUID!) {
    invoiceRequest(id: $id) {
      id
      status
      amount
      invoiceeType
      title
      taxNumber
      notifyEmail
      invoiceFiles
      issuedAt
      createTime
      user {
        name
        avatar
        gender
        mobile
        openid
      }
      orders {
        cashAmount
        order {
          id
          category
          amount
          createTime
          detail {
            __typename
            ... on SlotBookingList {
              items {
                courtName
                startTime
                price
              }
            }
            ... on EventSignupDetail {
              eventName
              gameFormat
              startTime
              duration
            }
            ... on CourseDetail {
              courseName
              hours
            }
          }
        }
      }
    }
  }
`

gql`
  mutation UploadInvoice($invoiceRequestId: ID!, $invoiceFiles: [String!]!) {
    uploadInvoice(invoiceRequestId: $invoiceRequestId, invoiceFiles: $invoiceFiles) {
      ... on InvoiceRequest {
        id
        status
        invoiceFiles
        issuedAt
      }
      ... on GraphqlRequestError {
        code
        message
      }
    }
  }
`

const formatTime = (value: string | null | undefined) =>
  value ? new Date(value).toLocaleString('zh-CN') : '-'

type OrderRow = NonNullable<
  NonNullable<GetInvoiceRequestDetailQuery['invoiceRequest']>['orders']
>[number]

const orderColumns: GridColDef<OrderRow>[] = [
  {
    field: 'category',
    headerName: '类型',
    width: 70,
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    valueGetter: (_value, row) => ORDER_TYPE_LABEL[row.order.category],
  },
  {
    field: 'detail',
    headerName: '详情',
    flex: 1,
    minWidth: 200,
    sortable: false,
    headerAlign: 'center',
    renderCell: ({ row }) => <OrderDetailCell detail={row.order.detail} />,
  },
  {
    field: 'createTime',
    headerName: '下单时间',
    width: 170,
    sortable: false,
    headerAlign: 'center',
    valueGetter: (_value, row) =>
      new Date(row.order.createTime).toLocaleString('zh-CN'),
  },
  {
    field: 'amount',
    headerName: '订单金额',
    width: 90,
    sortable: false,
    align: 'right',
    headerAlign: 'center',
    valueGetter: (_value, row) => `¥${row.order.amount}`,
  },
  {
    field: 'cashAmount',
    headerName: '开票金额',
    width: 90,
    sortable: false,
    align: 'right',
    headerAlign: 'center',
    valueGetter: (value: string) => `¥${value}`,
  },
]

interface InvoiceRequestDialogProps {
  invoiceRequestId: string | null
  onClose: () => void
}

export default function InvoiceRequestDialog({
  invoiceRequestId,
  onClose,
}: InvoiceRequestDialogProps) {
  const [invoiceFiles, setInvoiceFiles] = useState<string[]>([])
  const [uploading, setUploading] = useState(false)

  const { data, loading, refetch } = useQuery(GetInvoiceRequestDetailDocument, {
    variables: { id: invoiceRequestId ?? '' },
    skip: !invoiceRequestId,
    fetchPolicy: 'network-only',
  })

  const [uploadInvoice] = useMutation(UploadInvoiceDocument)

  const invoiceRequest = data?.invoiceRequest

  // Start from the files already saved on the request; reset per request.
  useEffect(() => {
    setInvoiceFiles(invoiceRequest?.invoiceFiles ?? [])
  }, [invoiceRequest])

  // FileField cannot restrict its picker to PDF, so drop anything else as it
  // lands. Uploaded names carry the extension of the file's MIME type.
  const handleInvoiceFilesChange = (files: string[] | null) => {
    const next = files ?? []
    const pdfs = next.filter((name) => name.toLowerCase().endsWith('.pdf'))
    if (pdfs.length < next.length) {
      message.error('请上传 PDF 发票文件')
    }
    setInvoiceFiles(pdfs)
  }

  const handleUpload = async () => {
    if (!invoiceRequestId || invoiceFiles.length === 0) {
      message.error('请先上传发票文件')
      return
    }

    setUploading(true)
    try {
      const result = await uploadInvoice({
        variables: {
          invoiceRequestId,
          invoiceFiles,
        },
      })

      const data = result.data?.uploadInvoice
      if (data?.__typename === 'InvoiceRequest') {
        message.success('发票上传成功')
        await refetch()
      } else if (data?.__typename === 'GraphqlRequestError') {
        message.error(data.message ?? '上传失败')
      }
    } catch (err) {
      message.error(`上传失败: ${err instanceof Error ? err.message : err}`)
    } finally {
      setUploading(false)
    }
  }

  return (
    <Dialog open={!!invoiceRequestId} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative',
        }}
      >
        发票申请详情
        <Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
          {invoiceRequest && (
            <Chip
              label={INVOICE_STATUS_CHIP[invoiceRequest.status].label}
              color={INVOICE_STATUS_CHIP[invoiceRequest.status].color}
              size="small"
            />
          )}
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex' }}>
          <IconButton onClick={() => refetch()} size="small" disabled={loading}>
            <RefreshIcon />
          </IconButton>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: 'grey.100', p: 1.5 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {invoiceRequest && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Invoice request details */}
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: 'text.secondary',
                    mb: 1,
                  }}
                >
                  申请信息
                </Typography>
                <InfoRow label="申请ID" value={invoiceRequest.id} />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 0.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    申请人
                  </Typography>
                  <UserAvatar user={invoiceRequest.user} />
                </Box>
                <InfoRow
                  label="抬头类型"
                  value={INVOICEE_TYPE_LABEL[invoiceRequest.invoiceeType]}
                />
                <InfoRow label="发票抬头" value={invoiceRequest.title} />
                <InfoRow label="税号" value={invoiceRequest.taxNumber || '-'} />
                <InfoRow label="接收邮箱" value={invoiceRequest.notifyEmail || '-'} />
                <InfoRow
                  label="申请时间"
                  value={formatTime(invoiceRequest.createTime)}
                />
                {invoiceRequest.issuedAt && (
                  <InfoRow
                    label="开票时间"
                    value={formatTime(invoiceRequest.issuedAt)}
                  />
                )}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 0.5,
                    mt: 1,
                    pt: 1,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    发票总额:
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: 'error.main',
                      fontWeight: 'bold',
                    }}
                  >
                    ¥{Number(invoiceRequest.amount).toFixed(2)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Associated orders */}
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: 'text.secondary',
                    mb: 1,
                  }}
                >
                  关联订单
                </Typography>
                <DataGridPro
                  rows={invoiceRequest.orders ?? []}
                  columns={orderColumns}
                  getRowId={(row: OrderRow) => row.order.id}
                  hideFooter
                  disableRowSelectionOnClick
                  disableColumnMenu
                  density="compact"
                  getRowHeight={() => 'auto'}
                  autoHeight
                  localeText={{ noRowsLabel: '暂无订单' }}
                  sx={{
                    '& .MuiDataGrid-cell': {
                      display: 'flex',
                      alignItems: 'center',
                      py: 1,
                    },
                  }}
                />
              </CardContent>
            </Card>

            {/* Invoice files */}
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                {invoiceRequest.status === InvoiceStatus.Pending ? (
                  <>
                    <FileField
                      label="发票文件"
                      allowNonImage
                      fullWidth
                      value={invoiceFiles}
                      onChange={handleInvoiceFilesChange}
                      helperText="上传开具的电子发票，仅支持 PDF 格式"
                    />
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        startIcon={
                          uploading ? <CircularProgress size={16} /> : <UploadIcon />
                        }
                        onClick={handleUpload}
                        disabled={uploading || invoiceFiles.length === 0}
                      >
                        上传发票
                      </Button>
                    </Box>
                  </>
                ) : (
                  <>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: 'text.secondary',
                        mb: 1,
                      }}
                    >
                      发票文件
                    </Typography>
                    {invoiceFiles.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {invoiceFiles.map((file) => (
                          <Typography key={file} variant="body2">
                            {file}
                          </Typography>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        暂无发票文件
                      </Typography>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
        }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ userSelect: 'text' }}>
        {value}
      </Typography>
    </Box>
  )
}
